import dayjs from "dayjs"
import { useMemo } from "react"

import { useGraphContext } from "../GraphContext"
import { useGetMgdlAsTopPosition } from "../useGetMgdlAsTopPosition"
import { extraMarkerHeight } from "./GraphActivity"
import { clamp } from "src/utils"

// if a glucose reading is within rowProximityAllowance of the row boundary, then we add an extra row as buffer
const rowProximityAllowance = 0

interface Analysis {
  rowsBelowGlucose: number;
  glucoseRows: number;
  rowsAboveGlucose: number;
}

/*
 * this hook analyzes the glucose data to tell us how much space we have above and below it
 * in which to place graph items
 */
export function useGraphActivitiesSpaceAnalysis(
  start: string,
  gridShiftEnabled: boolean,
  minGlucoseReading?: number,
  maxGlucoseReading?: number,
): Analysis {
  const {
    yAxisHeight,
    graphActivityHeight,
    graphStart,
    hoursPerDivider,
  } = useGraphContext()

  /*
   * the usable height for showing activities is the yAxisHeight minus the
   * extraMarkerHeight because we need room for the time markers to not get
   * clipped by the top and bottom of their containers
   */
  const usableHeight = yAxisHeight - extraMarkerHeight
  const numberOfRows = Math.floor(usableHeight / graphActivityHeight)

  // alternates between true and false for each column
  const gridShift: boolean = useMemo(
    () => {
      const hoursDiff = dayjs(start).diff(
        graphStart,
        "hours",
        true,
      )

      const columnNumber = Math.floor(hoursDiff / hoursPerDivider)
      return columnNumber % 2 === 0
    },
    [
      start,
      graphStart,
      hoursPerDivider,
    ],
  )

  const hasGlucoseEvents = !!(minGlucoseReading && maxGlucoseReading)
  const getMgdlAsTopPosition = useGetMgdlAsTopPosition()

  return useMemo(
    () => {
      const [minGlucoseTopPosition] = getMgdlAsTopPosition(
        minGlucoseReading || 0,
      )
      const [maxGlucoseTopPosition] = getMgdlAsTopPosition(
        maxGlucoseReading || 0,
      )

      // defaults in case there are no glucose events
      let glucoseRows = 0
      let rowsBelowGlucose = Math.ceil(numberOfRows / 2)
      let rowsAboveGlucose = numberOfRows - rowsBelowGlucose

      if (hasGlucoseEvents) {
        let minGlucoseRow = Math.floor(
          minGlucoseTopPosition / graphActivityHeight,
        )

        const minGlucoseRemainder =
          minGlucoseTopPosition / graphActivityHeight - minGlucoseRow

        if (minGlucoseRemainder <= rowProximityAllowance) {
          minGlucoseRow = minGlucoseRow + 1
        }

        let maxGlucoseRow = Math.floor(
          maxGlucoseTopPosition / graphActivityHeight,
        )

        const maxGlucoseRemainder =
          maxGlucoseTopPosition / graphActivityHeight - maxGlucoseRow

        if (maxGlucoseRemainder >= 1 - rowProximityAllowance) {
          maxGlucoseRow = maxGlucoseRow - 1
        }

        const highestUnavailableRow = clamp(
          minGlucoseRow,
          0,
          numberOfRows - 1,
        )
        const lowestUnavailableRow = clamp(
          maxGlucoseRow,
          0,
          numberOfRows - 1,
        )

        rowsBelowGlucose = numberOfRows - highestUnavailableRow - 1
        glucoseRows = highestUnavailableRow - lowestUnavailableRow + 1
        rowsAboveGlucose = lowestUnavailableRow
      }

      if (gridShiftEnabled) {
        // when grid shift is false, rows above glucose must start on an even numbered row
        // when grid shift is true, rows below glucose must start on an odd numbered row
        const rowsAboveEven = rowsAboveGlucose % 2 === 0
        const shiftRowsAboveGlucose =
          rowsAboveGlucose > 0 &&
          ((!gridShift && !rowsAboveEven) || (gridShift && rowsAboveEven))

        if (shiftRowsAboveGlucose) {
          rowsAboveGlucose--
          glucoseRows++
        }

        // when grid shift is true, rows above glucose must start on an even numbered row
        // when grid shift is false, rows above glucose must start on an odd numbered row
        const rowsBelowEven = (rowsAboveGlucose + glucoseRows) % 2 === 0
        const shiftRowsBelowGlucose =
          rowsBelowGlucose > 0 &&
          ((gridShift && !rowsBelowEven) || (!gridShift && rowsBelowEven))

        if (shiftRowsBelowGlucose) {
          rowsBelowGlucose--
          glucoseRows++
        }
      }

      return {
        rowsBelowGlucose,
        glucoseRows,
        rowsAboveGlucose,
      }
    },
    [
      hasGlucoseEvents,
      getMgdlAsTopPosition,
      minGlucoseReading,
      maxGlucoseReading,
      graphActivityHeight,
      gridShift,
      gridShiftEnabled,
      numberOfRows,
    ],
  )
}
